import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosRequestConfig } from "axios";
import { HttpError } from "@refinedev/core";
import { useAuth0Provider } from "@src/libs/auth0-provider";

export type UseSmartCustomQueryMethod = "GET" | "get";

export interface UseSmartCustomQueryConfig {
    url: string;
    method?: UseSmartCustomQueryMethod;
    config?: AxiosRequestConfig;
    dataProviderName?: string;
    queryOptions: {
        queryKey: any[];
        enabled?: boolean;
        retry?: number | boolean | ((failureCount: number, error: any) => boolean);
        cacheTime?: number;
        staleTime?: number;
        refetchOnWindowFocus?: boolean;
        refetchInterval?: number | false;
        onSuccess?: (data: any) => void;
        onError?: (error: any) => void;
    };
    // Refine properties (for future compatibility)
    meta?: any;
    metaData?: any;
    successNotification?: any;
    errorNotification?: any;
}

export interface UseSmartCustomQueryResult {
    // Query properties
    data?: any;
    status?: number;
    statusText?: string;
    error?: HttpError | null;
    isLoading: boolean;
    isError: boolean;
    isFetching: boolean;
    isSuccess: boolean;
    refetch: () => Promise<any>;

    // Custom enhanced functions
    debouncedRefetch: (timeout?: number, newConfig?: Partial<UseSmartCustomQueryConfig>) => Promise<void>;
    cancelQuery: () => void;
}

export const useSmartCustomQuery = (config: UseSmartCustomQueryConfig): UseSmartCustomQueryResult => {
    const { axios } = useAuth0Provider();
    const queryClient = useQueryClient();
    // State for managing loading when using refetchStrictly
    const [isStartLoading, setIsStartLoading] = useState(false);
    const [currentConfig, setCurrentConfig] = useState(config);
    const [customRetryCount, setCustomRetryCount] = useState(0);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const queryKey = currentConfig.queryOptions.queryKey;

    // Determine if query should be enabled based on basic conditions
    const isQueryEnabled = useMemo(() => {
        // User explicitly disabled
        if (currentConfig.queryOptions?.enabled === false) {
            return false;
        }

        // Basic requirements
        if (!currentConfig.url) {
            return false;
        }

        // Default to enabled if basic conditions are met
        return true;
    }, [currentConfig.url, currentConfig.queryOptions?.enabled]);

    // Setup useQuery with custom fetcher
    const queryResult = useQuery({
        queryKey,
        queryFn: async ({ signal }) => {
            const { headers, params } = config.config ?? {};
            const requestConfig: AxiosRequestConfig = {
                ...config,
                headers,
                params,
                signal,
            };
            const axiosResponse = await axios.get(currentConfig.url, requestConfig);
            return {
                data: axiosResponse.data,
                status: axiosResponse.status,
                statusText: axiosResponse.statusText,
            };
        },
        enabled: isQueryEnabled,
        retry: currentConfig.queryOptions?.retry ?? 0,
        onSuccess: (response) => {
            setIsStartLoading(false);
            if (response?.status === 202) {
                setCustomRetryCount((prev) => prev + 1);
            }
            // Handle success notification first
            if (currentConfig.successNotification) {
                if (typeof currentConfig.successNotification === "function") {
                    currentConfig.successNotification(response.data);
                } else {
                    // Handle notification object if provided
                    console.log("Success notification:", currentConfig.successNotification);
                }
            }
            // Call user's onSuccess callback
            currentConfig.queryOptions?.onSuccess?.(response.data);
        },
        onError: (error) => {
            setIsStartLoading(false);
            // Handle error notification first
            if (currentConfig.errorNotification) {
                if (typeof currentConfig.errorNotification === "function") {
                    currentConfig.errorNotification(error);
                } else {
                    // Handle notification object if provided
                    console.error("Error notification:", currentConfig.errorNotification);
                }
            }
            // Call user's onError callback
            currentConfig.queryOptions?.onError?.(error);
        },
    });

    // Debounced refetch function
    const debouncedRefetch = useCallback(
        (timeout = 0, newConfig?: Partial<UseSmartCustomQueryConfig>): Promise<void> => {
            setIsStartLoading(true);
            setCustomRetryCount(0);
            return new Promise((resolve, reject) => {
                // Clear existing timeout
                if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                }

                // Update config immediately if provided
                if (newConfig) {
                    setCurrentConfig((prev) => ({ ...prev, ...newConfig }));
                }

                // Set new timeout
                debounceTimeoutRef.current = setTimeout(async () => {
                    try {
                        if (queryKey.length > 0) {
                            queryClient.cancelQueries({ queryKey });
                        }

                        const data = await queryResult.refetch();

                        if (data.isError) {
                            reject(data.error);
                        } else {
                            resolve();
                        }
                    } catch (error) {
                        reject(error);
                    }
                }, timeout);
            });
        },
        [queryClient, queryKey, queryResult],
    );

    const cancelQuery = useCallback(() => {
        if (queryKey.length > 0) {
            queryClient.cancelQueries({ queryKey });
        }
    }, [queryClient, queryKey]);

    useEffect(() => {
        if (customRetryCount > 0) {
            queryResult.refetch();
        }
    }, [customRetryCount]);

    // Clean up debounce timeout on unmount
    useEffect(() => {
        return () => {
            queryClient.removeQueries({ queryKey });

            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [JSON.stringify(queryKey)]);

    // Sync external config changes with internal state
    useEffect(() => {
        setCurrentConfig(config);
    }, [JSON.stringify(config)]);

    return {
        data: queryResult.data?.data,
        status: queryResult.data?.status,
        statusText: queryResult.data?.statusText,
        error: queryResult.error as HttpError | null,
        isLoading: queryResult.isLoading || isStartLoading,
        isError: queryResult.isError,
        isFetching: queryResult.isFetching,
        isSuccess: queryResult.isSuccess,
        refetch: queryResult.refetch,
        debouncedRefetch,
        cancelQuery,
    };
};
